import { Box, FormControl } from '@mui/material'
import { IDefaultEntityData } from '../interfaces'
import { useTranslation } from 'react-i18next'
import { useEntityRecords } from '../../../hooks/useEntityRecords'
import SearchInAPIAndSelectInput from '../../Common/SearchInAPIAndSelectInput'
import { useAppSelector } from '../../../redux/hook'
import { findEntityByName } from '../../../utils/EntityUtils'
import { ReactElement, useCallback, useEffect, useRef } from 'react'
import { fetchEntityInstanceData } from '../../../services/entityInstanceServices'
import { EntityStatus } from '../../../constants/constants'

interface DefaultEntitySelectProps {
    orgId: string
    value: IDefaultEntityData | null
    onChange: (id: string, value: IDefaultEntityData | null) => void
    label: string
    entityTemplateName: string
    disabled: boolean
}

const DefaultEntitySelect = (props: DefaultEntitySelectProps): ReactElement => {
    const { orgId, value, onChange, label, entityTemplateName, disabled } =
        props
    const { t } = useTranslation()
    const navList = useAppSelector((state) => state.nav.navList)
    const templateId =
        findEntityByName(navList, entityTemplateName)?.entity?.id || ''
    const { recordsList, isFetching, hasMore, onSearch, fetchNextPage } =
        useEntityRecords({ templateId, orgId, id: label })
    const hasCalledApi = useRef(false)

    useEffect(() => {
        const getEntityUpdatedData = async () => {
            if (value?.entityId && value?.templateId && !hasCalledApi.current) {
                const entityInstanceResponse = await fetchEntityInstanceData(
                    orgId,
                    value.templateId,
                    value.entityId,
                )

                if (entityInstanceResponse?.status === EntityStatus.DELETE) {
                    onChange(label, null)
                } else {
                    const updatedUid =
                        entityInstanceResponse?.uid || value?.uid || ''

                    if (updatedUid !== value?.uid) {
                        onChange(label, {
                            ...value,
                            uid: updatedUid,
                        } as IDefaultEntityData)
                    }
                }
                hasCalledApi.current = true
            }
        }

        getEntityUpdatedData()
    }, [orgId, value?.entityId, value?.templateId, onChange, label])

    const onSelect = useCallback(
        (id: string, value: any) => {
            const entityValue: IDefaultEntityData | null = value
                ? {
                      uid: value?.uid,
                      entityId: value?.entityId,
                      templateId: value?.templateId,
                      templateName: value?.templateName,
                  }
                : null
            onChange(id, entityValue)
        },
        [onChange],
    )

    return (
        <Box>
            <FormControl fullWidth size="small">
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ width: '100%' }}>
                        <SearchInAPIAndSelectInput
                            id={`org-${label}-select`}
                            label={t(label)}
                            isLegend={true}
                            value={value}
                            list={recordsList}
                            onSelect={onSelect}
                            onSearch={onSearch}
                            onFetchNextPage={fetchNextPage}
                            hasMore={hasMore}
                            isFetching={isFetching}
                            itemId="entityId"
                            itemName="uid"
                            hasItemsEdit={false}
                            fieldSize="medium"
                            disabled={disabled}
                        />
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}

export default DefaultEntitySelect
